// (c) chaehongee. Confidential and proprietary.

import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { SessionInfoResponse as SessionInfo } from 'models-ts/session'
import type { NodeResponse } from 'models-ts/node'


interface SessionState {
  sessionsById: Record<string, SessionInfo>
  isLoadingBySessionId: Record<string, boolean>
  loadingMessageBySessionId: Record<string, string | null>
  errorBySessionId: Record<string, string | null>
}

const initialState: SessionState = {
  sessionsById: {},
  isLoadingBySessionId: {},
  loadingMessageBySessionId: {},
  errorBySessionId: {},
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    upsertSession: (state, action: PayloadAction<SessionInfo>) => {
      const s = action.payload
      if (!s?.id) return
      state.sessionsById[s.id] = s
      state.errorBySessionId[s.id] = null
    },
    removeSession: (state, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.sessionsById[id]
      delete state.isLoadingBySessionId[id]
      delete state.errorBySessionId[id]
    },
    updateSessionTitle: (
      state,
      action: PayloadAction<{ sessionId: string; title: string }>
    ) => {
      const { sessionId, title } = action.payload
      const s = state.sessionsById[sessionId]
      if (!s) return
      state.sessionsById[sessionId] = { ...s, title }
    },
    addNode: (
      state,
      action: PayloadAction<{ sessionId: string; node: NodeResponse }>
    ) => {
      const { sessionId, node } = action.payload
      const s = state.sessionsById[sessionId]
      if (!s) return
      const exists = s.nodes.some((n) => n.id === node.id)
      if (!exists) {
        const nextNodes = [...s.nodes, node]
        state.sessionsById[sessionId] = { ...s, nodes: nextNodes }
      }
    },
    mergeSessionEntities: (
      state,
      action: PayloadAction<{
        sessionId: string
        nodes?: NodeResponse[]
        commits?: any[]
      }>
    ) => {
      const { sessionId, nodes, commits } = action.payload
      const s = state.sessionsById[sessionId]
      if (!s) return
      let nextNodes = s.nodes
      let nextCommits = s.commits as any[]
      if (nodes && nodes.length) {
        const byId = new Map(s.nodes.map((n) => [n.id, n]))
        for (const n of nodes) byId.set(n.id, n)
        nextNodes = Array.from(byId.values())
      }
      if (commits && commits.length) {
        const byId = new Map(s.commits.map((c: any) => [c.id, c]))
        for (const c of commits) byId.set(c.id, c)
        nextCommits = Array.from(byId.values()) as any
      }
      if (nextNodes !== s.nodes || nextCommits !== s.commits) {
        state.sessionsById[sessionId] = {
          ...s,
          nodes: nextNodes,
          commits: nextCommits as any,
        }
      }
    },
    removeNode: (
      state,
      action: PayloadAction<{ sessionId: string; nodeId: string }>
    ) => {
      const { sessionId, nodeId } = action.payload
      const s = state.sessionsById[sessionId]
      if (!s) return
      const nextNodes = s.nodes.filter((n) => n.id !== nodeId)
      const nextCommits = (s.commits as any[]).filter((c: any) => {
        if (c.node_to_id === nodeId) return false
        const sources = (c as any).nodes_from_ids as string[] | undefined
        if (sources && sources.includes(nodeId)) return false
        return true
      }) as any
      state.sessionsById[sessionId] = { ...s, nodes: nextNodes, commits: nextCommits }
    },
    setSessionLoading: (
      state,
      action: PayloadAction<{ sessionId: string; isLoading: boolean }>
    ) => {
      const { sessionId, isLoading } = action.payload
      if (!sessionId) return
      state.isLoadingBySessionId[sessionId] = isLoading
      if (!isLoading) {
        delete state.loadingMessageBySessionId[sessionId]
      }
    },
    setSessionLoadingMessage: (
      state,
      action: PayloadAction<{ sessionId: string; message: string | null }>
    ) => {
      const { sessionId, message } = action.payload
      if (!sessionId) return
      state.loadingMessageBySessionId[sessionId] = message
    },
    setSessionError: (
      state,
      action: PayloadAction<{ sessionId: string; error: string | null }>
    ) => {
      const { sessionId, error } = action.payload
      if (!sessionId) return
      state.errorBySessionId[sessionId] = error
    },
  },
})

export const {
  upsertSession,
  removeSession,
  updateSessionTitle,
  addNode,
  mergeSessionEntities,
  removeNode,
  setSessionLoading,
  setSessionLoadingMessage,
  setSessionError,
} = sessionSlice.actions
export default sessionSlice.reducer
